import { useEffect, useState, type ReactNode } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { DeathScreen } from '@/widgets/death-screen'
import { reportError } from '@/shared/lib/telemetry'

export function RootErrorBoundary({ children }: { children: ReactNode }) {
  const [globalError, setGlobalError] = useState<unknown>(null)
  // `FallbackProps` библиотеки react-error-boundary отдаёт только `{ error, resetErrorBoundary }` —
  // componentStack в них физически нет, он приходит отдельно, в onError. Без этого состояния
  // дерево компонентов уходило бы только в консоль (reportError) и никогда — в отчёт DeathScreen.
  const [componentStack, setComponentStack] = useState<string | undefined>(undefined)

  useEffect(() => {
    function onError(e: ErrorEvent) {
      reportError(e.error ?? e.message)
      setGlobalError(e.error ?? new Error(e.message))
    }
    function onRejection(e: PromiseRejectionEvent) {
      reportError(e.reason)
      setGlobalError(e.reason)
    }
    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onRejection)
    return () => {
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onRejection)
    }
  }, [])

  if (globalError) return <DeathScreen error={globalError} />

  return (
    <ErrorBoundary
      FallbackComponent={({ error }) => <DeathScreen error={error} componentStack={componentStack} />}
      onError={(error, info) => {
        reportError(error, { componentStack: info.componentStack })
        setComponentStack(info.componentStack ?? undefined)
      }}
    >
      {children}
    </ErrorBoundary>
  )
}
